import { Component, OnInit } from '@angular/core';
import { DashboardStoreService } from '../services/dashboard-store.service';
import { SaleDash } from '../models/sale-dash';
import { Observable, Subject } from 'rxjs';
import * as Chart from 'chart.js';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-dash-sales',
  templateUrl: './dash-sales.component.html',
  styleUrls: ['./dash-sales.component.scss'],
})
export class DashSalesComponent implements OnInit {
  sale$: Observable<SaleDash>;

  // Charts
  conversionRateChart: Chart;
  plansChart: Chart;
  topSellersChart: Chart;

  private unsubscribe$ = new Subject();

  constructor(private dashboardStore: DashboardStoreService) {}

  ngOnInit() {
    this.buildCharts();
    this.getDashData();
  }

  getDashData() {
    this.sale$ = this.dashboardStore.sale$;
    this.sale$.pipe(takeUntil(this.unsubscribe$)).subscribe(data => this.fillCharts(data));
  }

  buildCharts() {
    // Conversion Rate Chart
    this.conversionRateChart = new Chart('conversionRateCanvas', {
      type: 'doughnut',
      options: {
        responsive: true,
        rotation: 1 * Math.PI,
        circumference: 1 * Math.PI,
        title: { display: true, text: 'Porcentagem de Conversão', defaultFontFamily: 'Lexend', fontSize: 15 },
        legend: { display: false, defaultFontFamily: 'Lexend' },
        tooltips: { enabled: false, defaultFontFamily: 'Lexend' },
      },
      plugins: [
        {
          beforeDraw: (chart: any) => {
            let width = chart.chart.width,
              height = chart.chart.height,
              ctx = chart.chart.ctx;

            ctx.restore();
            let fontSize = height / 60;
            ctx.font = fontSize + 'em Lexend';
            ctx.textBaseline = 'middle';
            ctx.fillStyle = this.getConversionRateChartColor(chart.data.datasets[0].data[0]);

            let text = chart.data.datasets[0].data[0].toString() + '%',
              textX = Math.round((width - ctx.measureText(text).width) / 2),
              textY = height * 0.9;

            ctx.fillText(text, textX, textY);
            ctx.save();
          },
        },
      ],
    });

    // Plans Chart
    this.plansChart = new Chart('plansCanvas', {
      type: 'pie',
      options: {
        responsive: true,
        title: { display: true, text: 'Top 10 Planos', defaultFontFamily: 'Lexend', fontSize: 15 },
        legend: { display: true, position: 'right', defaultFontFamily: 'Lexend' },
      },
      plugins: [
        {
          afterDraw: function (chart: any) {
            if (chart.data.datasets[0].data.length === 0) {
              var ctx = chart.chart.ctx;
              var width = chart.chart.width;
              var height = chart.chart.height;

              ctx.restore();
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              let fontSize = height / 150;
              ctx.font = fontSize + 'em Lexend';

              let text = 'Sem dados para exibir',
                textX = Math.round(width / 2),
                textY = height / 2;

              ctx.fillText(text, textX, textY);
              ctx.save();
            }
          },
        },
      ],
    });

    // Top Sellers Chart
    this.topSellersChart = new Chart('topSellersCanvas', {
      type: 'bar',
      options: {
        responsive: true,
        scales: { xAxes: [{ display: true }], yAxes: [{ display: true }] },
        title: { display: true, text: 'Top 10 Vendedores', defaultFontFamily: 'Lexend', fontSize: 15 },
        legend: { display: true, defaultFontFamily: 'Lexend' },
      },
    });
  }

  fillCharts(data: SaleDash) {
    // Conversion Rate Chart
    this.conversionRateChart.data.datasets = [
      {
        data: [data.conversionRate, 100 - data.conversionRate],
        backgroundColor: [this.getConversionRateChartColor(data.conversionRate), '#e6e6e6'],
      },
    ];
    this.conversionRateChart.update();

    // Plans Chart
    this.plansChart.data.labels = data.plans.map(plan => plan.label);
    this.plansChart.data.datasets = [
      {
        data: data.plans.map(plan => plan.value),
        backgroundColor: [
          '#F9CD45',
          '#E65209',
          '#FC1795',
          '#5109E6',
          '#B00864',
          '#21B06E',
          '#8AB021',
          '#00796B',
          '#0097A7',
          '#616161',
        ],
      },
    ];
    this.plansChart.update();

    // Top Sellers Chart
    this.topSellersChart.data.labels = data.topSellers.map(seller => seller.label.substr(0, 14).concat('...'));
    this.topSellersChart.data.datasets = [
      {
        data: data.topSellers.map(seller => seller.value),
        label: 'Valor (R$)',
        backgroundColor: '#1e9835',
        hoverBackgroundColor: '#72bf85',
      },
    ];
    this.topSellersChart.update();
  }

  getConversionRateChartColor(value: number) {
    let hexColor: string;

    if (value <= 30) {
      hexColor = '#FF0000';
    } else if (value > 30 && value <= 60) {
      hexColor = '#FFD700';
    } else {
      hexColor = '#199A3A';
    }

    return hexColor;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
